import React from 'react';
import { Box, SxProps, Theme } from '@mui/material';

export interface StatusFilterChipOption<V extends string = string> {
  value: V;
  label: string;
  /**
   * Optional accent dot rendered before the label. When the segment is
   * active it's swapped to white so the dot stays legible on the primary
   * fill.
   */
  color?: string;
}

interface StatusFilterChipsProps<V extends string> {
  value: V;
  options: ReadonlyArray<StatusFilterChipOption<V>>;
  onChange: (next: V) => void;
  /**
   * When true, the row scrolls horizontally instead of wrapping. Use on
   * narrow viewports where the chips don't fit on a single line.
   */
  scroll?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * Pill-shaped status filter — single bordered container with borderless
 * segments inside. Used by both the Vocabulary and Dictionary pages so the
 * status row reads as the same control everywhere. Active segment fills with
 * `primary.main`; inactive segments stay transparent. Optional color dot in
 * front of each label.
 */
const StatusFilterChips = <V extends string>({
  value,
  options,
  onChange,
  scroll,
  sx,
}: StatusFilterChipsProps<V>) => (
  <Box
    sx={[
      {
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'primary.tableBorder',
        borderRadius: 999,
        display: 'inline-flex',
        gap: '2px',
        maxWidth: scroll ? '100%' : undefined,
        overflowX: scroll ? 'auto' : 'visible',
        p: '3px',
        WebkitOverflowScrolling: scroll ? 'touch' : undefined,
      },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
  >
    {options.map((opt) => {
      const on = opt.value === value;
      return (
        <Box
          key={opt.value}
          component={'button'}
          onClick={() => onChange(opt.value)}
          sx={{
            alignItems: 'center',
            bgcolor: on ? 'primary.main' : 'transparent',
            border: 'none',
            borderRadius: 999,
            color: on ? 'common.white' : 'text.secondary',
            cursor: 'pointer',
            display: 'inline-flex',
            flex: 'none',
            fontFamily: 'inherit',
            fontSize: 13,
            fontWeight: 700,
            gap: 0.75,
            height: 32,
            px: 1.5,
            transition: 'all .14s ease',
            whiteSpace: 'nowrap',
          }}
        >
          {opt.color && (
            <Box
              sx={{
                bgcolor: on ? 'common.white' : opt.color,
                borderRadius: '50%',
                flex: 'none',
                height: 8,
                width: 8,
              }}
            />
          )}
          {opt.label}
        </Box>
      );
    })}
  </Box>
);

export default StatusFilterChips;
